import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Typography,
  Paper,
  RadioGroup,
  Radio,
  FormControlLabel,
  Checkbox,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Alert,
  Tooltip,
  IconButton,
  Tab,
  Tabs,
  TextField,
  Divider,
  Grid,
  Card,
  CardContent,
  CardActions,
  Stepper,
  Step,
  StepLabel,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Input
} from '@mui/material';
import InfoIcon from '@mui/icons-material/Info';
import LockIcon from '@mui/icons-material/Lock';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import AddIcon from '@mui/icons-material/Add';
import UploadIcon from '@mui/icons-material/Upload';
import KeyIcon from '@mui/icons-material/Key';
import FileUploadIcon from '@mui/icons-material/FileUpload';
import CreateIcon from '@mui/icons-material/Create';
import { 
  getSigningCredentials, 
  createSigningCredential, 
  uploadCertificate, 
  setDefaultSigningCredential 
} from '../../services/api';

/**
 * Component for managing and selecting credentials for digital signing
 */
const CredentialManager = ({ onCredentialSelect, selectedCredential, open, onClose }) => {
  const [credentials, setCredentials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentCredential, setCurrentCredential] = useState(selectedCredential || '');
  const [makeDefault, setMakeDefault] = useState(false);
  
  // Tab state
  const [tabValue, setTabValue] = useState(0);
  
  // Create credential state
  const [newCredentialName, setNewCredentialName] = useState('');
  const [newCredentialType, setNewCredentialType] = useState('RSA');
  const [newCredentialKeySize, setNewCredentialKeySize] = useState(2048);
  const [creationStep, setCreationStep] = useState(0);
  const [creationLoading, setCreationLoading] = useState(false);
  const [createdCredentialId, setCreatedCredentialId] = useState(null);

  // HSM configuration state
  const [hsmConfig, setHsmConfig] = useState({
    hsmType: 'thales',
    libraryPath: '/opt/safenet/protecttoolkit5/ptk/lib/libcryptoki.so',
    tokenLabel: 'DID_KEYS',
    pin: '',
    host: '',
    port: 1792,
    useSsl: true,
    keyLabel: ''
  });
  
  // Upload certificate state
  const [certificateFile, setCertificateFile] = useState(null);
  const [certificatePassword, setCertificatePassword] = useState('');
  const [certificateName, setCertificateName] = useState('');
  const [uploadStep, setUploadStep] = useState(0);
  const [uploadLoading, setUploadLoading] = useState(false);
  const [uploadedCredentialId, setUploadedCredentialId] = useState(null);

  // Fetch available credentials on component mount
  useEffect(() => {
    const fetchCredentials = async () => {
      try {
        setLoading(true);
        // Call API to get available credentials
        const data = await getSigningCredentials();
        setCredentials(data);
        
        // If no credential is selected but we have a default, select it
        if (!selectedCredential && data.find(cred => cred.isDefault)) {
          const defaultCred = data.find(cred => cred.isDefault);
          setCurrentCredential(defaultCred.id);
        }
      } catch (err) {
        console.error('Error fetching credentials:', err);
        setError(err.message || 'Failed to fetch credentials');
      } finally {
        setLoading(false);
      }
    };

    fetchCredentials();
  }, [selectedCredential]);

  // Tab change handler
  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const handleCredentialChange = (event) => {
    setCurrentCredential(event.target.value);
  };

  const handleMakeDefaultChange = (event) => {
    setMakeDefault(event.target.checked);
  };

  const handleConfirm = async () => {
    try {
      // If user wants to make this credential the default
      if (makeDefault) {
        await setDefaultSigningCredential(currentCredential);
      }
      
      // Pass the selected credential back to the parent component
      onCredentialSelect(currentCredential);
      onClose();
    } catch (err) {
      console.error('Error setting default credential:', err);
      setError('Failed to set default credential. Please try again.');
    }
  };
  
  // Create new credential handlers
  const handleCreateCredential = async () => {
    try {
      setCreationLoading(true);
      
      // Call API to create new credential
      const data = await createSigningCredential({
        name: newCredentialName,
        type: newCredentialType,
        keySize: newCredentialKeySize,
        isDefault: false
      });
      
      setCreatedCredentialId(data.id);
      
      // Move to next step
      setCreationStep(1);
      
      // Refresh credential list
      const refreshedCredentials = await getSigningCredentials();
      setCredentials(refreshedCredentials);
      setCurrentCredential(data.id);
    } catch (err) {
      console.error('Error creating credential:', err);
      setError('Failed to create credential. Please try again.');
    } finally {
      setCreationLoading(false);
    }
  };
  
  const handleCredentialNameChange = (e) => {
    setNewCredentialName(e.target.value);
  };
  
  const handleCredentialTypeChange = (e) => {
    setNewCredentialType(e.target.value);
  };
  
  const handleCredentialKeySizeChange = (e) => {
    setNewCredentialKeySize(e.target.value);
  };
  
  const handleCreationReset = () => {
    setCreationStep(0);
    setCreatedCredentialId(null);
    setNewCredentialName('');
    setNewCredentialType('RSA');
    setNewCredentialKeySize(2048);
  };
  
  // Upload certificate handlers
  const handleCertificateUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      setCertificateFile(file);
      
      // Auto-populate name if empty
      if (!certificateName) {
        setCertificateName(file.name.replace(/\.[^/.]+$/, ""));
      }
    }
  };
  
  const handleCertificateName = (e) => {
    setCertificateName(e.target.value);
  };
  
  const handleCertificatePassword = (e) => {
    setCertificatePassword(e.target.value);
  };
  
  const handleUploadCertificate = async () => {
    try {
      setUploadLoading(true);
      
      // Create FormData for file upload
      const formData = new FormData();
      formData.append('certificate', certificateFile);
      formData.append('name', certificateName);
      formData.append('password', certificatePassword);
      
      // Call API to upload certificate
      const data = await uploadCertificate(formData);
      setUploadedCredentialId(data.id);
      
      // Move to next step
      setUploadStep(1);
      
      // Refresh credential list
      const refreshedCredentials = await getSigningCredentials();
      setCredentials(refreshedCredentials);
      setCurrentCredential(data.id);
    } catch (err) {
      console.error('Error uploading certificate:', err);
      setError('Failed to upload certificate. Please try again.');
    } finally {
      setUploadLoading(false);
    }
  };
  
  const handleUploadReset = () => {
    setUploadStep(0);
    setUploadedCredentialId(null);
    setCertificateFile(null);
    setCertificateName('');
    setCertificatePassword('');
  };

  // If no real credentials available yet, use mockups for display
  const displayCredentials = credentials.length > 0 ? credentials : [
    {
      id: 'default-key',
      name: 'Default Signing Key',
      type: 'RSA',
      isDefault: true,
      securityLevel: 'Standard',
      description: 'Default RSA 2048-bit key generated during service initialization'
    }
  ];

  // Creating new credential section
  const renderCreateCredentialContent = () => {
    return (
      <Box sx={{ mt: 2 }}>
        <Stepper activeStep={creationStep} sx={{ mb: 3 }}>
          <Step>
            <StepLabel>Configure Credential</StepLabel>
          </Step>
          <Step>
            <StepLabel>Complete</StepLabel>
          </Step>
        </Stepper>
        
        {creationStep === 0 ? (
          <Box>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <TextField
                  label="Credential Name"
                  fullWidth
                  required
                  value={newCredentialName}
                  onChange={handleCredentialNameChange}
                  placeholder="My Signing Key"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel id="key-type-label">Key Type</InputLabel>
                  <Select
                    labelId="key-type-label"
                    value={newCredentialType}
                    onChange={handleCredentialTypeChange}
                    label="Key Type"
                  >
                    <MenuItem value="RSA">RSA</MenuItem>
                    <MenuItem value="ECDSA">ECDSA</MenuItem>
                    <MenuItem value="HSM">HSM (Hardware Security Module)</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              {newCredentialType !== 'HSM' && (
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth>
                    <InputLabel id="key-size-label">Key Size</InputLabel>
                    <Select
                      labelId="key-size-label"
                      value={newCredentialKeySize}
                      onChange={handleCredentialKeySizeChange}
                      label="Key Size"
                    >
                      {newCredentialType === 'RSA' ? (
                        <>
                          <MenuItem value={2048}>2048 bits</MenuItem>
                          <MenuItem value={3072}>3072 bits</MenuItem>
                          <MenuItem value={4096}>4096 bits</MenuItem>
                        </>
                      ) : (
                        <>
                          <MenuItem value={256}>P-256</MenuItem>
                          <MenuItem value={384}>P-384</MenuItem>
                          <MenuItem value={521}>P-521</MenuItem>
                        </>
                      )}
                    </Select>
                  </FormControl>
                </Grid>
              )}
            </Grid>

            {/* HSM Configuration Fields */}
            {newCredentialType === 'HSM' && (
              <Box sx={{ mt: 3 }}>
                <Divider sx={{ mb: 2 }} />
                <Typography variant="subtitle2" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
                  <LockIcon sx={{ mr: 1, fontSize: 18 }} />
                  HSM Configuration (Thales DPoD)
                </Typography>
                <Grid container spacing={2} sx={{ mt: 1 }}>
                  <Grid item xs={12} sm={6}>
                    <FormControl fullWidth size="small">
                      <InputLabel id="hsm-type-label">HSM Type</InputLabel>
                      <Select
                        labelId="hsm-type-label"
                        value={hsmConfig.hsmType}
                        onChange={(e) => setHsmConfig({...hsmConfig, hsmType: e.target.value})}
                        label="HSM Type"
                      >
                        <MenuItem value="thales">Thales Luna / DPoD</MenuItem>
                        <MenuItem value="aws">AWS CloudHSM</MenuItem>
                        <MenuItem value="azure">Azure Dedicated HSM</MenuItem>
                        <MenuItem value="softhsm">SoftHSM (Development)</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="Token Label"
                      fullWidth
                      size="small"
                      value={hsmConfig.tokenLabel}
                      onChange={(e) => setHsmConfig({...hsmConfig, tokenLabel: e.target.value})}
                      placeholder="DID_KEYS"
                    />
                  </Grid>
                  <Grid item xs={12} sm={8}>
                    <TextField
                      label="HSM Host"
                      fullWidth
                      size="small"
                      value={hsmConfig.host}
                      onChange={(e) => setHsmConfig({...hsmConfig, host: e.target.value})}
                      placeholder="hsm.your-org.com"
                    />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <TextField
                      label="Port"
                      fullWidth
                      size="small"
                      type="number"
                      value={hsmConfig.port}
                      onChange={(e) => setHsmConfig({...hsmConfig, port: parseInt(e.target.value)})}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="Key Label"
                      fullWidth
                      size="small"
                      value={hsmConfig.keyLabel}
                      onChange={(e) => setHsmConfig({...hsmConfig, keyLabel: e.target.value})}
                      placeholder="did-web-key-001"
                      helperText="Label for the signing key in HSM"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="HSM PIN"
                      fullWidth
                      size="small"
                      type="password"
                      value={hsmConfig.pin}
                      onChange={(e) => setHsmConfig({...hsmConfig, pin: e.target.value})}
                      helperText="Partition PIN for authentication"
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      label="PKCS#11 Library Path"
                      fullWidth
                      size="small"
                      value={hsmConfig.libraryPath}
                      onChange={(e) => setHsmConfig({...hsmConfig, libraryPath: e.target.value})}
                      helperText="Path to the HSM PKCS#11 library"
                    />
                  </Grid>
                </Grid>
                <Alert severity="info" sx={{ mt: 2 }}>
                  HSM credentials provide enterprise-grade key protection. Keys never leave the hardware module.
                </Alert>
              </Box>
            )}
            
            <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
              <Button
                variant="contained"
                onClick={handleCreateCredential}
                disabled={!newCredentialName || creationLoading}
                startIcon={creationLoading ? <CircularProgress size={20} /> : <CreateIcon />}
              >
                Generate Credential
              </Button>
            </Box>
          </Box>
        ) : (
          <Box>
            <Alert severity="success" sx={{ mb: 3 }}>
              Credential created successfully!
            </Alert>
            
            <Typography variant="subtitle1" gutterBottom>
              New Credential Details
            </Typography>
            
            <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
              <Typography variant="body2">
                <strong>Name:</strong> {newCredentialName}
              </Typography>
              <Typography variant="body2">
                <strong>Type:</strong> {newCredentialType} {newCredentialKeySize} bits
              </Typography>
              <Typography variant="body2">
                <strong>Security Level:</strong> Standard
              </Typography>
            </Paper>
            
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
              <Button onClick={handleCreationReset}>
                Create Another
              </Button>
              <Button 
                variant="contained" 
                onClick={() => {
                  setTabValue(0);
                  setCurrentCredential(createdCredentialId);
                }}
              >
                Use This Credential
              </Button>
            </Box>
          </Box>
        )}
      </Box>
    );
  };
  
  // Upload certificate section
  const renderUploadCertificateContent = () => {
    return (
      <Box sx={{ mt: 2 }}>
        <Stepper activeStep={uploadStep} sx={{ mb: 3 }}>
          <Step>
            <StepLabel>Upload Certificate</StepLabel>
          </Step>
          <Step>
            <StepLabel>Complete</StepLabel>
          </Step>
        </Stepper>
        
        {uploadStep === 0 ? (
          <Box>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <Box 
                  sx={{ 
                    border: '2px dashed #ccc',
                    borderRadius: 2,
                    p: 3,
                    textAlign: 'center',
                    mb: 2,
                    cursor: 'pointer',
                    '&:hover': {
                      bgcolor: 'action.hover'
                    }
                  }}
                  component="label"
                >
                  <Input
                    accept=".pfx,.p12,.crt,.pem,.key"
                    type="file"
                    sx={{ display: 'none' }}
                    onChange={handleCertificateUpload}
                  />
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                    <FileUploadIcon fontSize="large" color="primary" />
                    <Typography variant="body1" sx={{ mt: 1 }}>
                      {certificateFile ? certificateFile.name : 'Click to upload certificate file'}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
                      Supports PFX, P12, PEM, CRT, and KEY formats
                    </Typography>
                  </Box>
                </Box>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="Certificate Friendly Name"
                  fullWidth
                  required
                  value={certificateName}
                  onChange={handleCertificateName}
                  placeholder="My Certificate"
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="Certificate Password (if required)"
                  fullWidth
                  type="password"
                  value={certificatePassword}
                  onChange={handleCertificatePassword}
                  placeholder="Leave empty if no password"
                />
              </Grid>
            </Grid>
            
            <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
              <Button
                variant="contained"
                onClick={handleUploadCertificate}
                disabled={!certificateFile || !certificateName || uploadLoading}
                startIcon={uploadLoading ? <CircularProgress size={20} /> : <UploadIcon />}
              >
                Upload Certificate
              </Button>
            </Box>
          </Box>
        ) : (
          <Box>
            <Alert severity="success" sx={{ mb: 3 }}>
              Certificate uploaded successfully!
            </Alert>
            
            <Typography variant="subtitle1" gutterBottom>
              Uploaded Certificate Details
            </Typography>
            
            <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
              <Typography variant="body2">
                <strong>Name:</strong> {certificateName}
              </Typography>
              <Typography variant="body2">
                <strong>File:</strong> {certificateFile?.name}
              </Typography>
              <Typography variant="body2">
                <strong>Security Level:</strong> High
              </Typography>
            </Paper>
            
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
              <Button onClick={handleUploadReset}>
                Upload Another
              </Button>
              <Button 
                variant="contained" 
                onClick={() => {
                  setTabValue(0);
                  setCurrentCredential(uploadedCredentialId);
                }}
              >
                Use This Certificate
              </Button>
            </Box>
          </Box>
        )}
      </Box>
    );
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box display="flex" alignItems="center">
          <LockIcon sx={{ mr: 1 }} />
          Select Signing Credential
        </Box>
      </DialogTitle>
      <DialogContent>
        {loading && tabValue === 0 ? (
          <Box display="flex" justifyContent="center" my={3}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        ) : (
          <>
            <Tabs value={tabValue} onChange={handleTabChange} sx={{ mb: 2 }}>
              <Tab label="Select Existing" icon={<KeyIcon />} iconPosition="start" />
              <Tab label="Create New" icon={<AddIcon />} iconPosition="start" />
              <Tab label="Upload Certificate" icon={<UploadIcon />} iconPosition="start" />
            </Tabs>
            
            {tabValue === 0 && (
              <>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Choose the credential you want to use for signing your verifiable credentials. Different credentials offer varying levels of security.
                  <Tooltip title="Verifiable credentials rely on cryptographic signing to verify authenticity. Higher security credentials provide stronger trust signals.">
                    <IconButton size="small" sx={{ ml: 1 }}>
                      <HelpOutlineIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Typography>

                <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
                  <RadioGroup
                    value={currentCredential}
                    onChange={handleCredentialChange}
                  >
                    {displayCredentials.map((credential) => (
                      <Box key={credential.id} sx={{ mb: 2, p: 1, borderRadius: 1, bgcolor: credential.isDefault ? 'action.selected' : 'transparent' }}>
                        <FormControlLabel
                          value={credential.id}
                          control={<Radio />}
                          label={
                            <Box>
                              <Typography variant="subtitle1">
                                {credential.name}
                                {credential.isDefault && (
                                  <Typography component="span" variant="caption" sx={{ ml: 1, p: 0.5, bgcolor: 'primary.light', color: 'white', borderRadius: 1 }}>
                                    Default
                                  </Typography>
                                )}
                              </Typography>
                              <Typography variant="body2" color="text.secondary">
                                {credential.description}
                              </Typography>
                              <Box display="flex" alignItems="center" mt={1}>
                                <Typography variant="caption" color="text.secondary" sx={{ mr: 1 }}>
                                  Security Level:
                                </Typography>
                                <Tooltip title={`${credential.securityLevel} security level for content signing`}>
                                  <Typography 
                                    variant="caption" 
                                    sx={{ 
                                      p: 0.5, 
                                      borderRadius: 1,
                                      bgcolor: 
                                        credential.securityLevel === 'High' ? 'success.light' :
                                        credential.securityLevel === 'Enterprise' ? 'info.light' : 'warning.light',
                                      color: 'white'
                                    }}
                                  >
                                    {credential.securityLevel}
                                  </Typography>
                                </Tooltip>
                              </Box>
                            </Box>
                          }
                        />
                      </Box>
                    ))}
                  </RadioGroup>
                </Paper>

                <FormControlLabel
                  control={
                    <Checkbox
                      checked={makeDefault}
                      onChange={handleMakeDefaultChange}
                      color="primary"
                    />
                  }
                  label="Set as default credential for future signings"
                />
              </>
            )}
            
            {tabValue === 1 && renderCreateCredentialContent()}
            
            {tabValue === 2 && renderUploadCertificateContent()}
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="primary">
          Cancel
        </Button>
        {tabValue === 0 && (
          <Button
            onClick={handleConfirm}
            variant="contained"
            color="primary"
            disabled={loading || !currentCredential}
          >
            Use Selected Credential
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default CredentialManager;